import { roundHalfUp } from "./money";
import { VAT_RATE_PERCENT } from "./vat";

const PERCENT_DIVISOR = 100;

/** What one pricebook line is priced from. */
export interface PricebookPriceInput {
  /** Price per unit including VAT, before any discount. */
  unitPrice: number;
  /** The line's own discount %, or null to follow the pricebook's. */
  itemDiscountPercent: number | null;
  pricebookDiscountPercent: number;
}

export interface PricebookPrice {
  /** The discount % that was applied: the line's own when it has one, else the pricebook's. */
  discountPercent: number;
  priceInclVat: number;
  priceExVat: number;
}

/**
 * The selling price of a pricebook line, with and without VAT — a port of the backend's
 * `computePricebookPrice` (`src/utils/pricebookPrice.util.ts`), so the price shown while
 * editing is the one the server stores:
 *
 *   d              = item discount % ?? pricebook discount %
 *   price_incl_vat = round2(unit_price × (100 − d) / 100)
 *   price_ex_vat   = round2(price_incl_vat × 100 / 107)
 *
 * The ex-VAT price is taken from the ROUNDED inclusive price, as the quotations do. Both
 * roundings are half-up at 2 dp through `roundHalfUp`.
 */
export const computePricebookPrice = ({
  unitPrice,
  itemDiscountPercent,
  pricebookDiscountPercent,
}: PricebookPriceInput): PricebookPrice => {
  const discountPercent = itemDiscountPercent ?? pricebookDiscountPercent;
  const priceInclVat = roundHalfUp(
    (unitPrice * (PERCENT_DIVISOR - discountPercent)) / PERCENT_DIVISOR,
  );
  const priceExVat = roundHalfUp(
    (priceInclVat * PERCENT_DIVISOR) / (PERCENT_DIVISOR + VAT_RATE_PERCENT),
  );
  return { discountPercent, priceInclVat, priceExVat };
};
